import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, ExternalLink, Loader2, RefreshCw } from "lucide-react";
import {
  getFreeAfterCashbackReport,
  type FreeAfterCashbackProduct,
  type FreeAfterCashbackStore,
} from "../../services/FreeAfterCashbackService";

// Admin → Monitor → Free after cash back. Layout follows the Users tab: an
// overview strip whose counts are the filters, then a list on the left and
// one product fully worked out on the right (buy here, claim this, net that).

type Ceiling = 0 | 1 | 3;
type SortKey = "net" | "stores" | "carried" | "ibotta" | "name";

const RETAILER_LABEL: Record<string, string> = { walmart: "Walmart", dollar_general: "Dollar General" };
const IBOTTA_LINK: Record<string, string> = {
  walmart: "https://ibotta.com/retailers/Walmart/4",
  dollar_general: "https://ibotta.com/retailers/Dollar-General/34",
};

const CARD = "bg-white dark:bg-dark-800 rounded-2xl ring-1 ring-gray-200 dark:ring-dark-700 shadow-sm";
const LABEL = "text-[10px] font-bold uppercase tracking-[0.14em] text-gray-400 dark:text-dark-400";
const EPS = 1e-9;

const money = (v: number) => `$${Math.abs(v).toFixed(2)}`;
const netText = (e: number) => (e < 0 ? `+${money(e)}` : e > 0 ? money(e) : "Free");
const fmtTime = (iso?: string) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

function productUrl(p: FreeAfterCashbackProduct): string | undefined {
  const url = p.best.url;
  if (!url) return undefined;
  if (p.retailer === "walmart" && url.startsWith("/")) return `https://www.walmart.com${url}`;
  return url;
}

function storesOf(p: FreeAfterCashbackProduct): FreeAfterCashbackStore[] {
  const list = p.allStores?.length
    ? p.allStores
    : p.hits.map((h) => ({ storeId: h.storeId, price: h.price, effective: h.effective, inStock: h.inStock, quantity: h.quantity, dgCoupon: h.dgCoupon }));
  return [...list].sort(
    (a, b) => Number(b.inStock) - Number(a.inStock) || a.effective - b.effective || a.storeId.localeCompare(b.storeId, undefined, { numeric: true }),
  );
}

function rewardOf(p: FreeAfterCashbackProduct): { amount: number; source: "Walmart Cash" | "Ibotta"; perUnitIbotta: number } {
  const b = p.best;
  const perUnitIbotta = (b.ibottaAmount ?? 0) / (b.ibottaQty || 1);
  const wm = b.walmartCash ?? 0;
  if (wm > 0 && wm >= perUnitIbotta) return { amount: wm, source: "Walmart Cash", perUnitIbotta };
  return { amount: perUnitIbotta, source: "Ibotta", perUnitIbotta };
}

export default function FreeAfterCashbackTab() {
  const [ceiling, setCeiling] = useState<Ceiling>(1);
  const [retailer, setRetailer] = useState<string>("");
  const [multiOnly, setMultiOnly] = useState(false);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("net");
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["admin-free-after-cashback"],
    queryFn: () => getFreeAfterCashbackReport(false),
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });

  const products = useMemo(() => data?.products ?? [], [data?.products]);

  const counts = useMemo(() => {
    const under = (max: number) => products.filter((p) => p.best.effective <= max + EPS);
    const atCeiling = under(ceiling);
    return {
      free: under(0).length,
      dollar: under(1).length,
      three: under(3).length,
      walmart: atCeiling.filter((p) => p.retailer === "walmart").length,
      dg: atCeiling.filter((p) => p.retailer === "dollar_general").length,
      multi: atCeiling.filter((p) => p.storeCount >= 2).length,
    };
  }, [products, ceiling]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = products.filter((p) => {
      if (p.best.effective > ceiling + EPS) return false;
      if (retailer && p.retailer !== retailer) return false;
      if (multiOnly && p.storeCount < 2) return false;
      if (!q) return true;
      return (
        p.name.toLowerCase().includes(q) ||
        p.upc.includes(q) ||
        (p.best.ibottaOffer ?? "").toLowerCase().includes(q) ||
        (p.allStores ?? []).some((s) => s.storeId === q) ||
        p.stores.some((s) => s === q)
      );
    });
    return [...rows].sort((a, b) => {
      switch (sortKey) {
        case "stores":
          return b.storeCount - a.storeCount || a.best.effective - b.best.effective;
        case "carried":
          return (b.carriedStores ?? 0) - (a.carriedStores ?? 0) || a.best.effective - b.best.effective;
        case "ibotta":
          return (b.best.ibottaAmount ?? 0) - (a.best.ibottaAmount ?? 0) || a.best.effective - b.best.effective;
        case "name":
          return a.name.localeCompare(b.name);
        default:
          return a.best.effective - b.best.effective || b.storeCount - a.storeCount || a.name.localeCompare(b.name);
      }
    });
  }, [products, ceiling, retailer, multiOnly, search, sortKey]);

  const keyOf = (p: FreeAfterCashbackProduct) => `${p.retailer}|${p.upc}`;
  const selected = visible.find((p) => keyOf(p) === selectedKey) ?? visible[0] ?? null;

  useEffect(() => {
    if (selected && keyOf(selected) !== selectedKey) setSelectedKey(keyOf(selected));
  }, [selected, selectedKey]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-dark-400 py-10 justify-center">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading the latest report…
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="rounded-2xl ring-1 ring-amber-200 dark:ring-amber-900/40 bg-amber-50 dark:bg-amber-900/20 p-4 text-sm text-amber-800 dark:text-amber-200 flex gap-2">
        <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
        <div>
          <div className="font-semibold">No report yet</div>
          <div className="mt-1 opacity-80">
            {error instanceof Error ? error.message : "The daily scan has not published a report."} It runs every morning on the monitoring host
            (<code>free-after-cashback -s3</code>).
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* ── Header ─────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-bold tracking-tight text-gray-900 dark:text-white">Free after cash back</h2>
          <p className="text-xs text-gray-500 dark:text-dark-300 mt-0.5">
            Scanned {fmtTime(data.generatedAt)} · {data.storesScanned.toLocaleString()} store queries · {data.rowsRead.toLocaleString()} catalog rows ·
            Ibotta snapshot {fmtTime(data.snapshotWrittenAt)} ({data.offersInMatcher.toLocaleString()} offers)
            {data.failedQueries ? ` · ${data.failedQueries} queries failed` : ""}
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-600 dark:text-dark-200 ring-1 ring-gray-200 dark:ring-dark-600 bg-white dark:bg-dark-800 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* ── Overview strip: counts are the filters ─────────── */}
      <div className={`${CARD} px-4 py-3 flex items-center gap-4`}>
        <button
          type="button"
          onClick={() => setCeiling(0)}
          className={`shrink-0 px-2.5 py-1 -my-1 -mx-1 rounded-xl text-left transition-colors ${ceiling === 0 ? "bg-emerald-50 dark:bg-emerald-900/20" : "hover:bg-gray-50 dark:hover:bg-dark-700/60"}`}
          title="Show only items that are free or better"
        >
          <p className="text-2xl font-mono font-bold tabular-nums leading-tight text-emerald-600 dark:text-emerald-400">{counts.free}</p>
          <p className={LABEL}>Free or better</p>
        </button>
        <div className="w-px self-stretch my-0.5 bg-gray-100 dark:bg-dark-700 shrink-0" />
        <div className="flex-1 min-w-0 flex flex-wrap items-center gap-1.5">
          <Chip label="Free" count={counts.free} dot="bg-emerald-500" active={ceiling === 0} onClick={() => setCeiling(0)} />
          <Chip label="≤ $1" count={counts.dollar} dot="bg-sky-500" active={ceiling === 1} onClick={() => setCeiling(1)} />
          <Chip label="≤ $3" count={counts.three} dot="bg-violet-500" active={ceiling === 3} onClick={() => setCeiling(3)} />
          <div className="w-px h-5 bg-gray-100 dark:bg-dark-700 mx-1.5 shrink-0" />
          <Chip label="Walmart" count={counts.walmart} dot="bg-sky-600" active={retailer === "walmart"} onClick={() => setRetailer(retailer === "walmart" ? "" : "walmart")} />
          <Chip label="Dollar General" count={counts.dg} dot="bg-amber-500" active={retailer === "dollar_general"} onClick={() => setRetailer(retailer === "dollar_general" ? "" : "dollar_general")} />
          <div className="w-px h-5 bg-gray-100 dark:bg-dark-700 mx-1.5 shrink-0" />
          <Chip label="Deal at 2+ stores" count={counts.multi} dot="bg-gray-400" active={multiOnly} onClick={() => setMultiOnly((v) => !v)} />
        </div>
        <div className="w-px self-stretch my-0.5 bg-gray-100 dark:bg-dark-700 shrink-0 hidden md:block" />
        <div className="hidden md:flex items-center gap-2">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, UPC, store #"
            className="w-52 px-2.5 py-1.5 text-xs bg-white dark:bg-dark-800 ring-1 ring-gray-200 dark:ring-dark-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-dark-500"
          />
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value as SortKey)}
            className="px-2 py-1.5 text-xs rounded-lg ring-1 ring-gray-200 dark:ring-dark-600 bg-white dark:bg-dark-800 text-gray-700 dark:text-dark-200 focus:outline-none"
            title="Sort"
          >
            <option value="net">Biggest profit</option>
            <option value="stores">Most deal stores</option>
            <option value="carried">Most stores carrying</option>
            <option value="ibotta">Biggest Ibotta offer</option>
            <option value="name">Name A–Z</option>
          </select>
        </div>
      </div>
      <div className="md:hidden flex gap-2">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, UPC, store #"
          className="flex-1 px-2.5 py-1.5 text-xs bg-white dark:bg-dark-800 ring-1 ring-gray-200 dark:ring-dark-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-dark-500"
        />
        <select
          value={sortKey}
          onChange={(e) => setSortKey(e.target.value as SortKey)}
          className="px-2 py-1.5 text-xs rounded-lg ring-1 ring-gray-200 dark:ring-dark-600 bg-white dark:bg-dark-800 text-gray-700 dark:text-dark-200"
        >
          <option value="net">Biggest profit</option>
          <option value="stores">Most deal stores</option>
          <option value="carried">Most stores carrying</option>
          <option value="ibotta">Biggest Ibotta offer</option>
          <option value="name">Name A–Z</option>
        </select>
      </div>

      {/* ── List + detail ───────────────────────────────────── */}
      {visible.length === 0 ? (
        <div className={`${CARD} py-10 text-center text-sm text-gray-500 dark:text-dark-400`}>Nothing matches these filters.</div>
      ) : (
        <div className="grid lg:grid-cols-[minmax(300px,380px)_minmax(0,1fr)] gap-4 items-start">
          <div className={`${CARD} overflow-hidden divide-y divide-gray-100 dark:divide-dark-700 max-h-[calc(100vh-14rem)] overflow-y-auto`}>
            <div className="px-3 py-2 flex items-center justify-between sticky top-0 bg-white dark:bg-dark-800 z-10">
              <p className={LABEL}>{visible.length} products</p>
              <p className={LABEL}>net</p>
            </div>
            {visible.map((p) => {
              const key = keyOf(p);
              const active = selected ? keyOf(selected) === key : false;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSelectedKey(key)}
                  className={`w-full text-left flex items-center gap-3 px-3 py-2.5 transition-colors ${active ? "bg-red-50/60 dark:bg-red-900/10 shadow-[inset_3px_0_0_#dc2626]" : "hover:bg-gray-50 dark:hover:bg-dark-700/60"}`}
                >
                  <Thumb src={p.image} className="w-10 h-10 rounded-lg p-0.5" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[12.5px] font-semibold leading-snug text-gray-900 dark:text-white line-clamp-2">{p.name}</p>
                    <p className="text-[11px] text-gray-500 dark:text-dark-300 mt-0.5">
                      {RETAILER_LABEL[p.retailer] ?? p.retailer} · {p.storeCount} deal store{p.storeCount === 1 ? "" : "s"} · <span className="font-mono">{money(p.best.price)}</span>
                    </p>
                  </div>
                  <NetPill effective={p.best.effective} />
                </button>
              );
            })}
          </div>

          {selected && <Detail product={selected} ceiling={ceiling} />}
        </div>
      )}
    </div>
  );
}

function Detail({ product: p, ceiling }: { product: FreeAfterCashbackProduct; ceiling: number }) {
  const [showAll, setShowAll] = useState(false);
  const b = p.best;
  const stores = storesOf(p);
  const best = stores[0];
  const reward = rewardOf(p);
  const url = productUrl(p);
  const price = best?.price ?? b.price;
  const net = best?.effective ?? b.effective;
  const shown = showAll ? stores : stores.slice(0, 24);
  const carried = p.carriedStores ?? p.storeCount;
  const inStock = p.carriedInStock ?? p.inStockStores;

  return (
    <div className="space-y-4 min-w-0">
      <div className={`${CARD} p-4`}>
        <div className="flex gap-4">
          <Thumb src={p.image} className="w-28 h-28 rounded-xl p-1.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">
              <h3 className="text-[15px] font-bold leading-snug text-gray-900 dark:text-white">{p.name}</h3>
              <NetPill effective={net} big />
            </div>
            <div className="flex flex-wrap items-center gap-1.5 mt-2 text-[11px] text-gray-500 dark:text-dark-300">
              <RetailerPill retailer={p.retailer} />
              {b.dgCoupon && (
                <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
                  DG coupon{b.offerText ? ` · ${b.offerText}` : ""}
                </span>
              )}
              <span className="font-mono">UPC {p.upc}</span>
              {b.category && (
                <>
                  <span>·</span>
                  <span>{b.category}</span>
                </>
              )}
              <span>·</span>
              {url && (
                <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sky-600 dark:text-sky-400 font-semibold hover:underline">
                  Product page <ExternalLink className="w-3 h-3" />
                </a>
              )}
              <a href={IBOTTA_LINK[p.retailer] ?? "https://ibotta.com"} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sky-600 dark:text-sky-400 font-semibold hover:underline">
                Ibotta offers <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            <div className="mt-3 grid sm:grid-cols-3 gap-2">
              <Step
                label="1 · Buy"
                value={
                  <>
                    {RETAILER_LABEL[p.retailer] ?? p.retailer} #{best?.storeId ?? b.storeId} · <span className="font-mono">{money(price)}</span>
                  </>
                }
                sub={`${best?.quantity ? `${best.quantity} on hand · ` : best && !best.inStock ? "out of stock at last crawl · " : ""}shelf ${money(p.priceLow || price)}${p.priceHigh > p.priceLow ? `–${money(p.priceHigh)}` : ""} across ${carried} store${carried === 1 ? "" : "s"}`}
              />
              <Step
                label="2 · Claim"
                value={
                  <>
                    {reward.source} <span className="font-mono">{money(reward.amount)}</span>
                    {reward.source === "Walmart Cash" && (b.walmartCashQty ?? 0) > 1 ? ` per item (buy ${b.walmartCashQty})` : ""}
                    {reward.source === "Ibotta" && (b.ibottaQty ?? 1) > 1 ? ` each when you buy ${b.ibottaQty}` : ""}
                  </>
                }
                sub={reward.source === "Walmart Cash" && !b.ibottaOffer
                  ? "Activate the reward in the Walmart app before checkout"
                  : `“${b.ibottaOffer ?? ""}”${(b.walmartCash ?? 0) > 0 && (b.ibottaAmount ?? 0) > 0 ? " · same offer in both apps, use one" : ""}${reward.source === "Walmart Cash" ? " · activate in the Walmart app before checkout" : " · add in Ibotta, then scan the receipt"} · listed until ${b.ibottaExpires ?? "?"}${b.ibottaExpired ? " (past, likely renewed)" : ""}`}
              />
              <Step
                label="3 · Net"
                value={
                  <span className="font-mono">
                    {money(price)} − {money(reward.amount)} = {net < 0 ? `+${money(net)}` : money(net)}
                  </span>
                }
                sub={`${net < 0 ? "you come out ahead" : net > 0 ? "out of pocket" : "free"} · ${p.storeCount} of ${carried} stores at this price`}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <Stat label="Deal stores" value={String(p.storeCount)} sub={`at or under ${ceiling === 0 ? "$0" : `$${ceiling}`}`} />
        <Stat label="Carried at" value={String(carried)} sub={`${inStock} in stock at last crawl`} />
        <Stat label="Shelf range" value={`${money(p.priceLow || price)}${p.priceHigh > p.priceLow ? ` – ${money(p.priceHigh)}` : ""}`} sub="across every crawled store" />
      </div>

      <div className={`${CARD} p-4`}>
        <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
          <p className={LABEL}>Stores · price → what you end up paying</p>
          <span className="text-[11px] text-gray-400 dark:text-dark-400">solid = at or under ceiling · dashed = pricier · faded = out of stock</span>
        </div>
        <div className="flex flex-wrap gap-1">
          {shown.map((s) => {
            const over = s.effective > ceiling + EPS;
            return (
              <span
                key={s.storeId}
                title={`${s.inStock ? (s.quantity ? `${s.quantity} on hand` : "in stock") : "out of stock"}${s.dgCoupon ? ", DG coupon price" : ""}`}
                className={`font-mono text-[10.5px] tabular-nums px-1.5 py-0.5 rounded-md border ${over ? "border-dashed border-gray-300 dark:border-dark-600 text-gray-400 dark:text-dark-400" : "border-emerald-200 dark:border-emerald-800 bg-emerald-50/60 dark:bg-emerald-900/10 text-gray-700 dark:text-dark-100"} ${s.inStock ? "" : "opacity-40"}`}
              >
                #{s.storeId} {money(s.price)} → {s.effective === 0 ? "free" : netText(s.effective)}
              </span>
            );
          })}
          {stores.length > 24 && (
            <button type="button" onClick={() => setShowAll((v) => !v)} className="text-[10.5px] text-sky-600 dark:text-sky-400 font-semibold px-1 py-0.5 hover:underline">
              {showAll ? "Show fewer" : `+${stores.length - 24} more`}
            </button>
          )}
        </div>
      </div>

      {b.ibottaTerms && (
        <div className={`${CARD} p-4`}>
          <p className={`${LABEL} mb-1`}>Ibotta terms</p>
          <p className="text-[12px] text-gray-600 dark:text-dark-200 whitespace-pre-line">{b.ibottaTerms}</p>
        </div>
      )}
    </div>
  );
}

function Chip({ label, count, dot, active, onClick }: { label: string; count: number; dot: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1.5 pl-2 pr-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap transition-colors ${active
        ? "bg-gray-900 dark:bg-white text-white/80 dark:text-dark-900/80 ring-1 ring-gray-900 dark:ring-white"
        : "bg-white dark:bg-dark-800 text-gray-500 dark:text-dark-300 ring-1 ring-gray-200 dark:ring-dark-600 hover:ring-gray-300 dark:hover:ring-dark-500 hover:text-gray-700 dark:hover:text-dark-100"
        }`}
      title={`Filter: ${label}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dot}`} />
      {label}
      <span className={`font-mono font-bold tabular-nums ${active ? "text-white dark:text-dark-900" : "text-gray-900 dark:text-white"}`}>{count}</span>
    </button>
  );
}

function NetPill({ effective, big }: { effective: number; big?: boolean }) {
  const tone =
    effective <= 0
      ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 ring-emerald-200 dark:ring-emerald-800"
      : "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 ring-amber-200 dark:ring-amber-800";
  return (
    <span className={`inline-flex items-center shrink-0 font-mono font-bold tabular-nums rounded-lg ring-1 ${tone} ${big ? "text-sm px-2.5 py-1" : "text-xs px-2 py-0.5"}`}>
      {netText(effective)}
    </span>
  );
}

function RetailerPill({ retailer }: { retailer: string }) {
  const cls =
    retailer === "walmart"
      ? "bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300"
      : "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300";
  return <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${cls}`}>{RETAILER_LABEL[retailer] ?? retailer}</span>;
}

function Thumb({ src, className }: { src?: string; className: string }) {
  return (
    <div className={`bg-white ring-1 ring-gray-100 dark:ring-dark-700 flex items-center justify-center overflow-hidden ${className}`}>
      {src ? <img src={src} alt="" loading="lazy" className="max-w-full max-h-full object-contain" /> : <span className="w-3 h-3 rounded-sm bg-gray-200 dark:bg-dark-600" />}
    </div>
  );
}

function Step({ label, value, sub }: { label: string; value: React.ReactNode; sub: string }) {
  return (
    <div className="rounded-xl bg-gray-50 dark:bg-dark-900/60 ring-1 ring-gray-100 dark:ring-dark-700 px-3 py-2 min-w-0">
      <p className={LABEL}>{label}</p>
      <p className="text-[13px] font-semibold mt-0.5 text-gray-900 dark:text-white">{value}</p>
      <p className="text-[11px] text-gray-500 dark:text-dark-300 mt-0.5">{sub}</p>
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className={`${CARD} px-4 py-3`}>
      <p className={LABEL}>{label}</p>
      <p className="text-xl font-mono font-bold tabular-nums mt-0.5 text-gray-900 dark:text-white">{value}</p>
      <p className="text-[11px] text-gray-500 dark:text-dark-300">{sub}</p>
    </div>
  );
}
